import { Extension } from '@codemirror/state';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronSelectorVerticalIcon,
  HelpCircleIcon,
  PlusIcon,
  XIcon,
} from '@langchain/untitled-ui-icons';
import { Checkbox, Option, Select } from '@mui/joy';
import Button from '@mui/joy/Button';

import { useEffect, useId, useMemo, useState } from 'react';

import { Tooltip } from '@/design-system';
import InfoCircleIcon from '@/icons/InfoCircleIcon.svg?react';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import { DatasetTransformationSchema } from '@/types/schema';
import { OpenAIFunction, emptyOpenAIFunction } from '@/utils/ai-tools';
import { cn } from '@/utils/tailwind';

import { useLanguagePreference } from '../../hooks/useLanguagePreferenceContext/useLanguagePreference';
import { CodeData, CodeDataEditor } from '../Code/Code';
import { CopyIconButton } from '../CopyButton/CopyButton';
import { TRANSFORMATION_TYPE_TO_LABEL } from '../DatasetCrudPane/constants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { MultipleArrayInput } from '../MultipleArrayInput';
import { PlainTextEditor } from '../PlainTextEditor';
import { JsonSchema7 } from '../SchemaEditor/JsonSchema';
import { isJsonSchema7 } from '../SchemaEditor/JsonSchema.guard';
import {
  VARIANT_LABEL_MAP,
  requiredEntriesToRequired,
} from '../SchemaEditor/schemaUtils';
import { SchemaEditorVariant } from '../SchemaEditor/types';
import { useEditorModeSelect } from './hooks';
import { FunctionParameterEditorViewProps } from './types';
import {
  changeSchemaType,
  getAvailableTransformations,
  getTypeValue,
  isJsonSchemaType,
  mapTransformationPathMatches,
  supportedTypes,
} from './utils';

export function TransformationsEditor({
  value,
  path,
  transformations,
  setTransformations,
  isArrayChild,
}: {
  value: JsonSchema7;
  path: string[];
  transformations?: DatasetTransformationSchema[];
  setTransformations: (transformations: DatasetTransformationSchema[]) => void;
  isArrayChild?: boolean;
}) {
  const transformationsForPath = transformations?.filter(
    // ignore first element in path which is 'inputs' or 'outputs'
    (t) => t.path.join('.') === path.join('.')
  );

  const availableTransformations = getAvailableTransformations(
    value,
    path,
    isArrayChild
  );

  const unselectedTransformations = availableTransformations.filter(
    (t) => !transformationsForPath?.find((t2) => t2.transformation_type === t)
  );

  return availableTransformations.length > 0 ? (
    <div className="my-2 flex flex-col items-start gap-2 text-xs">
      <div className="flex items-center gap-2 text-sm font-medium">
        Transformations
        <DropdownMenu>
          <DropdownMenuTrigger
            asChild
            disabled={unselectedTransformations.length === 0}
          >
            <button
              type="button"
              className="flex items-center gap-1 rounded-lg p-1 hover:bg-secondary disabled:opacity-50"
            >
              <PlusIcon className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <div className="flex items-center gap-2 p-1.5 text-xs font-semibold">
              Available transformations
              <a
                href="https://docs.langchain.com/langsmith/dataset-transformations"
                target="_blank"
              >
                <HelpCircleIcon className="size-4" />
              </a>
            </div>

            {unselectedTransformations.map((t) => (
              <DropdownMenuItem
                key={t}
                className="text-xs"
                onClick={() => {
                  setTransformations?.([
                    ...(transformations ?? []),
                    { path, transformation_type: t },
                  ]);
                }}
              >
                {TRANSFORMATION_TYPE_TO_LABEL[t]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {Boolean(transformationsForPath?.length) && (
        <div className="flex gap-2">
          {transformationsForPath?.map((t) => (
            <div
              key={t.transformation_type}
              className="flex items-center gap-1 rounded-lg border border-primary p-1 px-2"
            >
              {TRANSFORMATION_TYPE_TO_LABEL[t.transformation_type]}
              <button
                type="button"
                onClick={() => {
                  setTransformations(
                    transformations?.filter((prev) => prev !== t) ?? []
                  );
                }}
              >
                <XIcon className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;
}

export function EditInputFunctionsList(props: {
  value: Array<OpenAIFunction>;
  onChange: (value: Array<OpenAIFunction>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      {props.value.map((fn, idx) => (
        <EditInputFunctionListItem
          key={idx}
          value={fn}
          onChange={(value) => {
            props.onChange(props.value.map((v, i) => (i === idx ? value : v)));
          }}
          onRemove={() => {
            props.onChange(props.value.filter((_, i) => i !== idx));
          }}
        />
      ))}
      <Button
        size="sm"
        variant="outlined"
        color="neutral"
        sx={{ alignSelf: 'flex-start' }}
        startDecorator={<PlusIcon className="size-4" />}
        onClick={() => {
          props.onChange([...props.value, emptyOpenAIFunction()]);
        }}
      >
        Add Function
      </Button>
    </div>
  );
}

function EditInputFunctionListItem(props: {
  value: OpenAIFunction;
  onChange: (value: OpenAIFunction) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex flex-col rounded-md border border-secondary bg-background px-3.5 py-3"
      >
        <span className="font-medium">{props.value.name}</span>
        <span className="mt-1 -translate-x-[1px] text-tertiary">
          {props.value.description}
        </span>
      </button>
    );
  }

  return (
    <EditInputFunction
      value={props.value}
      onChange={props.onChange}
      onRemove={props.onRemove}
      onCancel={() => setOpen(false)}
    />
  );
}

export function EditorModeSelect({
  mode,
  setIsEditor,
  setLanguage,
  readOnly,
  isEditorAvailable,
}: ReturnType<typeof useEditorModeSelect> & { readOnly: boolean }) {
  return (
    <Select
      value={mode}
      onChange={(_, value) => {
        if (value == null) return;

        if (value === 'editor') {
          setIsEditor(true);
        } else {
          setIsEditor(false);
          setLanguage(value);
        }
      }}
      size="sm"
      disabled={readOnly}
      slotProps={{
        root: {
          sx: {
            backgroundColor: 'transparent',
            border: 'none',
            fontSize: 'small',
            flex: 'none',
          },
        },
        button: {
          sx: (theme) => ({
            color: theme.vars.palette.neutral[500],
            fontWeight: 600,
          }),
        },
      }}
      indicator={
        <ChevronSelectorVerticalIcon className="h-4 w-4 text-tertiary" />
      }
    >
      <Option disabled={!isEditorAvailable} value="editor">
        Editor
      </Option>
      <Option value="json">JSON</Option>
      <Option value="yaml">YAML</Option>
    </Select>
  );
}

function FunctionParameterTypeSelect(props: {
  value: string | string[] | undefined;
  onChange: (value: string | string[] | undefined) => void;
  readOnly: boolean;
  disabled?: boolean;
  allowTools?: boolean;
}) {
  const id = useId();
  const value = Array.isArray(props.value)
    ? props.value
    : props.value != null
    ? [props.value]
    : [];

  return (
    <div className="flex flex-col gap-1">
      <DropdownMenu open={props.readOnly ? false : undefined}>
        <DropdownMenuTrigger
          id={id}
          disabled={props.readOnly}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'group flex items-center justify-between gap-1 rounded-md border border-secondary p-1.5 px-2.5 text-left capitalize transition-colors hover:bg-secondary focus:outline-none',
            'px-2 py-0.5 text-sm',
            'disabled:pointer-events-none disabled:opacity-50'
          )}
        >
          <span>{value.join(', ')}</span>
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 transition-all group-data-[state=open]:rotate-180'
            )}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
          {supportedTypes
            .filter((type) => props.allowTools || type !== 'tool')
            .map((type) => (
              <DropdownMenuItem
                key={type}
                onClick={(e) => {
                  // click is propagated from the only button so just select the current item
                  if (
                    e.target instanceof HTMLButtonElement &&
                    e.target.name === 'only'
                  ) {
                    props.onChange([type]);
                    return;
                  }

                  e.preventDefault();
                  const op = value.includes(type) ? 'remove' : 'add';
                  let newValue: string[] | string = [...value];

                  if (op === 'add') {
                    newValue.push(type);
                  } else if (op === 'remove') {
                    if (newValue.length > 1) {
                      newValue = newValue.filter((i) => i !== type);
                    }
                  }

                  if (newValue.length > 0) {
                    props.onChange(newValue);
                  }
                }}
                className="group flex items-center justify-between gap-2 capitalize hover:bg-none"
                aria-checked={value.includes(type)}
              >
                <span className={'text-sm'}>{type}</span>
                <div className="relative flex w-12 items-center justify-end gap-1">
                  <button
                    name="only"
                    type="button"
                    className="absolute rounded-md bg-success-primary px-2 py-0.5 text-xs uppercase text-secondary opacity-0 hover:bg-success-secondary group-hover:opacity-100"
                  >
                    only
                  </button>
                  <CheckIcon
                    className={cn(
                      'absolute h-4 w-4 opacity-0 group-hover:hidden group-aria-checked:opacity-100'
                    )}
                  />
                </div>
              </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const TYPE_VALIDATION = {
  string: (text) => !!text.trim(),
  integer: (text) => !!text.trim() && text.match(/^\d+$/) != null,
  number: (text) => !!text.trim() && text.match(/^\d+(\.\d+)?$/) != null,
};
function EnumEditor({
  value,
  onChange,
  readOnly,
  type,
}: {
  value: JsonSchema7;
  onChange: (value: JsonSchema7) => void;
  readOnly: boolean;
  type?: string | string[];
}) {
  const handleChange = (mValue: string[]) => {
    let newEnums: string[] | number[] = mValue;
    if (type === 'number') {
      newEnums = newEnums.map((v) => parseFloat(v));
    } else if (type === 'integer') {
      newEnums = newEnums.map((v) => parseInt(v));
    }
    onChange({ ...value, enum: newEnums });
  };

  const validate =
    typeof type === 'string' && type ? TYPE_VALIDATION[type] : undefined;

  return (
    <>
      <div className="flex items-center gap-2">
        <Checkbox
          size="sm"
          checked={value.enum != null}
          onChange={(e) => {
            onChange({ ...value, enum: e.target.checked ? [] : undefined });
          }}
          label="Set allowed values"
          disabled={readOnly}
        />
        <Tooltip
          title="Press Enter or Tab to add a new value. Use commas when pasting multiple values."
          side="bottom"
        >
          <span>
            <InfoCircleIcon className="h-3.5 w-3.5 text-disabled" />
          </span>
        </Tooltip>
      </div>
      {value.enum && (
        <MultipleArrayInput
          onChange={handleChange}
          value={value.enum}
          disabled={readOnly}
          validate={validate}
        />
      )}
    </>
  );
}
function ObjectEditor({
  value,
  onChange,
  readOnly,
  codeEditorExtensions,
  nestingLevel,
  path,
  transformations,
  setTransformations,
}: {
  value: JsonSchema7;
  onChange: (value: JsonSchema7) => void;
  readOnly: boolean;
  codeEditorExtensions?: Record<string, Extension[]>;
  nestingLevel: number;
  path: string[];
  transformations?: DatasetTransformationSchema[];
  setTransformations?: (transformations: DatasetTransformationSchema[]) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex flex-col space-y-4">
      <button
        type="button"
        className={cn(
          'flex items-center gap-1.5 text-sm font-medium',
          collapsed && '-mb-4 '
        )}
        onClick={() => setCollapsed(!collapsed)}
      >
        <ChevronRightIcon
          className={cn(
            'h-4 w-4 transition duration-150',
            collapsed ? '' : 'rotate-90'
          )}
        />
        Properties
      </button>
      <div
        className={cn(
          'flex flex-col gap-1 overflow-hidden transition-all duration-300',
          collapsed ? 'max-h-0 opacity-0' : 'max-h-auto opacity-100'
        )}
      >
        <FunctionParametersEditor
          value={value}
          onChange={(value) =>
            onChange({ ...value, properties: value.properties })
          }
          readOnly={readOnly}
          variant={SchemaEditorVariant.NESTED}
          codeEditorExtensions={codeEditorExtensions}
          nestingLevel={nestingLevel + 1}
          path={path}
          transformations={transformations}
          setTransformations={setTransformations}
        />
      </div>
    </div>
  );
}

function ArrayEditor({
  items = {},
  onChange,
  readOnly,
  codeEditorExtensions,
  nestingLevel,
  path,
  transformations,
  setTransformations,
}: {
  items?: JsonSchema7 | JsonSchema7[];
  onChange: (value?: JsonSchema7 | JsonSchema7[]) => void;
  readOnly: boolean;
  codeEditorExtensions?: Record<string, Extension[]>;
  nestingLevel: number;
  path: string[];
  transformations?: DatasetTransformationSchema[];
  setTransformations?: (transformations: DatasetTransformationSchema[]) => void;
}) {
  const isItemsArray = items instanceof Array;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-1">
        <label className="text-sm font-medium">Items</label>
        <div className="flex items-center gap-1">
          {!isItemsArray && (
            <FunctionParameterTypeSelect
              readOnly={readOnly}
              value={getTypeValue(items)}
              onChange={(type) => {
                const newValue = changeSchemaType(items, type);
                onChange({
                  ...newValue,
                });
                if (setTransformations && transformations) {
                  // // Update transformations paths that match the old key
                  const oldKey = path[path.length - 1];
                  const newTransformations = mapTransformationPathMatches(
                    oldKey,
                    path.slice(0, -1),
                    transformations,
                    () => null
                  );
                  setTransformations(newTransformations);
                }
              }}
              allowTools={true}
            />
          )}
        </div>
      </div>

      {!isItemsArray && (
        <FunctionParameterSimpleEditor
          value={items}
          onChange={(value) => onChange(value)}
          readOnly={readOnly}
          codeEditorExtensions={codeEditorExtensions}
          nestingLevel={nestingLevel}
          path={path}
          transformations={transformations}
          setTransformations={setTransformations}
        />
      )}
      {!isItemsArray && setTransformations && (
        <TransformationsEditor
          path={path}
          transformations={transformations}
          setTransformations={setTransformations}
          value={items}
          isArrayChild={true}
        />
      )}
    </div>
  );
}

function FunctionParameterSimpleEditor({
  value,
  onChange,
  readOnly,
  codeEditorExtensions,
  nestingLevel,
  path,
  transformations,
  setTransformations,
}: {
  value: JsonSchema7;
  onChange: (value: JsonSchema7) => void;
  readOnly: boolean;
  codeEditorExtensions?: Record<string, Extension[]>;
  nestingLevel: number;
  path: string[];
  transformations?: DatasetTransformationSchema[];
  setTransformations?: (transformations: DatasetTransformationSchema[]) => void;
}) {
  const handledProperties = [
    'type',
    'description',
    'required',
    'additionalProperties',
    'enum',
    'strict',
    '$ref',
    'anyOf',
  ];
  if (isJsonSchemaType(value.type, 'object')) {
    handledProperties.push('properties');
  }

  if (isJsonSchemaType(value.type, 'array')) {
    handledProperties.push('items');
  }

  const unhandledValue = Object.fromEntries(
    Object.entries(value).filter(([key]) => !handledProperties.includes(key))
  );

  const hasUnhandledValue = Object.keys(unhandledValue).length > 0;

  const allowsEnums = ['string', 'integer', 'number'].some((t) =>
    isJsonSchemaType(value.type, t)
  );

  useEffect(() => {
    if (value.enum && !allowsEnums) {
      onChange({ ...value, enum: undefined });
    }
  }, [allowsEnums, onChange, value]);
  return (
    (['string', 'integer', 'number', 'object', 'array'].some((t) =>
      isJsonSchemaType(value.type, t)
    ) ||
      (Array.isArray(value.anyOf) &&
        value.anyOf.some((schema) =>
          ['string', 'integer', 'number', 'object', 'array'].some((t) =>
            isJsonSchemaType(schema.type, t)
          )
        )) ||
      hasUnhandledValue) && (
      <div className="flex flex-col gap-3">
        {allowsEnums && (
          <EnumEditor
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            type={value.type}
          />
        )}
        {isJsonSchemaType(value.type, 'object') && (
          <ObjectEditor
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            codeEditorExtensions={codeEditorExtensions}
            nestingLevel={nestingLevel}
            path={path}
            transformations={transformations}
            setTransformations={setTransformations}
          />
        )}

        {(isJsonSchemaType(value.type, 'array') ||
          (value.anyOf && value.anyOf.some((t) => t.type === 'array'))) && (
          <ArrayEditor
            items={
              isJsonSchemaType(value.type, 'array')
                ? value.items
                : value.anyOf?.find((t) => t.type === 'array')?.items
            }
            onChange={(items) => {
              if (isJsonSchemaType(value.type, 'array')) {
                onChange({ ...value, items });
              } else {
                onChange({
                  ...value,
                  anyOf: value.anyOf?.map((schema) =>
                    schema.type === 'array' ? { ...schema, items } : schema
                  ),
                });
              }
            }}
            readOnly={readOnly}
            codeEditorExtensions={codeEditorExtensions}
            nestingLevel={nestingLevel}
            path={path}
            transformations={transformations}
            setTransformations={setTransformations}
          />
        )}

        {hasUnhandledValue && (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Remaining keywords</label>

            <div className="rounded-md border border-secondary">
              <CodeData
                value={unhandledValue}
                readOnly={readOnly}
                onChange={(unhandledValue) => {
                  onChange({
                    ...unhandledValue,
                    ...Object.fromEntries(
                      Object.entries(value).filter(([key]) =>
                        handledProperties.includes(key)
                      )
                    ),
                  });
                }}
              />
            </div>
          </div>
        )}
      </div>
    )
  );
}

function AddPropertyToObjectButton({
  value,
  onChange,
  newItemType,
  addLabel,
}: {
  value: JsonSchema7;
  onChange: (value: JsonSchema7, newKey: string) => void;
  newItemType: 'boolean' | 'string';
  addLabel: string;
}) {
  return (
    <button
      className="flex items-center gap-2 self-start rounded-md border border-secondary p-1 pr-3 text-sm font-semibold hover:bg-secondary-hover active:bg-secondary"
      type="button"
      onClick={() => {
        const generateUniqueKey = (
          baseKey: string,
          properties: Record<string, any>
        ): string => {
          let counter = 0;
          let newKey = `${baseKey}${counter}`;
          while (newKey in properties) {
            counter++;
            newKey = `${baseKey}${counter}`;
          }
          return newKey;
        };

        const newKey = generateUniqueKey('new', value.properties ?? {});
        const newProperties = {
          ...value.properties,
          [newKey]: {
            type: newItemType,
          },
        };

        const newRequired = value.required && [...value.required];
        if (newRequired != null) newRequired.push(newKey);
        onChange(
          {
            ...value,
            properties: newProperties,
            required: newRequired,
          },
          newKey
        );
      }}
      data-testid="add-property-button"
    >
      <PlusIcon className="h-5 w-5" />
      <span>{addLabel}</span>
    </button>
  );
}

export function ParametersEditorView({
  value,
  variant,
  readOnly,
  codeEditorExtensions,
  propertyKey,
  onItemChange,
  onInputChange,
  onRequiredChange,
  onDelete,
  nestingLevel,

  required,
  isDuplicate,
  path = [],
  transformations,
  setTransformations,
}: FunctionParameterEditorViewProps) {
  const bgColor = nestingLevel % 2 === 0 ? 'bg-secondary' : 'bg-background';

  return (
    <div className={cn('rounded-md', bgColor)}>
      <div className="flex flex-col gap-1 px-5 py-3">
        <div className={cn('flex items-center gap-2')}>
          <Tooltip
            title={isDuplicate ? 'Duplicate key' : ''}
            side="top"
            align="start"
          >
            <input
              className={cn(
                'w-[1.75rem] flex-grow border-none bg-transparent font-mono font-medium outline-none placeholder:text-disabled',
                isDuplicate && 'text-error'
              )}
              value={propertyKey}
              placeholder="Argument name..."
              readOnly={readOnly}
              onChange={onInputChange}
            />
          </Tooltip>

          <Checkbox
            size="sm"
            checked={required ?? false}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={onRequiredChange}
            label="Required"
            className="mr-2"
          />

          <FunctionParameterTypeSelect
            readOnly={readOnly}
            value={getTypeValue(value)}
            onChange={(type) => {
              const newValue = changeSchemaType(value, type);
              onItemChange({
                ...newValue,
              });
              if (setTransformations && transformations) {
                // // Update transformations paths that match the old key
                const oldKey = propertyKey;
                const newTransformations = mapTransformationPathMatches(
                  oldKey,
                  path,
                  transformations,
                  () => null
                );
                setTransformations(newTransformations);
              }
            }}
          />

          <div className="-mr-2 flex items-center">
            {!readOnly && (
              <button
                type="button"
                onClick={onDelete}
                className="flex h-[26px] w-[26px] items-center justify-center rounded p-1 transition-all hover:bg-secondary-hover"
              >
                <TrashIcon />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-start justify-end pr-2">
          <PlainTextEditor
            placeholder={`Description of ${VARIANT_LABEL_MAP[variant]}...`}
            className="whitespace-pre-wrap border-transparent bg-transparent text-sm text-tertiary outline-none"
            textareaClassName={cn('text-sm')}
            value={value.description}
            onChange={(description) =>
              onItemChange({
                ...value,
                description,
              })
            }
            readOnly={readOnly}
          />
        </div>

        <FunctionParameterSimpleEditor
          value={value}
          onChange={onItemChange}
          readOnly={readOnly}
          codeEditorExtensions={codeEditorExtensions}
          nestingLevel={nestingLevel}
          path={[...path, propertyKey]}
          transformations={transformations}
          setTransformations={setTransformations}
        />

        {setTransformations && (
          <TransformationsEditor
            path={[...path, propertyKey]}
            transformations={transformations}
            setTransformations={setTransformations}
            value={value}
          />
        )}
      </div>
    </div>
  );
}

function FunctionParametersEditor({
  value,
  onChange,
  variant,
  readOnly,
  codeEditorExtensions,
  nestingLevel,
  path,
  transformations,
  setTransformations,
}: {
  value: JsonSchema7;
  onChange: (value: JsonSchema7) => void;
  variant: SchemaEditorVariant;
  readOnly: boolean;
  codeEditorExtensions?: Record<string, Extension[]>;
  nestingLevel: number;
  path: string[];
  transformations?: DatasetTransformationSchema[];
  setTransformations?: (transformations: DatasetTransformationSchema[]) => void;
}) {
  const properties = useMemo(() => value.properties ?? {}, [value.properties]);

  const [propertyEntries, setPropertyEntries] = useState(() =>
    Object.entries(properties ?? {})
  );
  const [requiredEntries, setRequiredEntries] = useState(() =>
    propertyEntries.map(([key]) => ({
      key,
      required: value.required?.includes(key) ?? false,
    }))
  );

  const propKeys = propertyEntries.map(([key]) => key);
  const [orderKeys, setOrderKeys] = useState(() => propKeys);

  useEffect(() => {
    const newPropertyEntries = Object.entries(properties ?? {});
    setPropertyEntries(newPropertyEntries);
    setRequiredEntries(
      newPropertyEntries.map(([key]) => ({
        key,
        required: value.required?.includes(key) ?? false,
      }))
    );
    setOrderKeys(newPropertyEntries.map(([key]) => key));
  }, [properties, value.required]);

  const handleChildChange = (newValue: JsonSchema7, idx: number) => {
    const updatedEntries = [...propertyEntries];
    updatedEntries[idx][1] = newValue;
    setPropertyEntries(updatedEntries);

    const updatedValue = {
      ...value,
      properties: Object.fromEntries(updatedEntries),
    };
    onChange(updatedValue);
  };

  const handleInputChange = (newKey: string, idx: number) => {
    if (setTransformations && transformations) {
      // Update transformations paths that match the old key
      const oldKey = propertyEntries[idx][0];
      const changedIdx = path.length;
      const newTransformations = mapTransformationPathMatches(
        oldKey,
        path,
        transformations,
        (t) => ({
          ...t,
          path: t.path.map((segment, idx) => {
            if (segment === oldKey && idx === changedIdx) {
              return newKey;
            }
            return segment;
          }),
        })
      );
      setTransformations(newTransformations);
    }

    const updatedEntries = [...propertyEntries];
    updatedEntries[idx][0] = newKey;

    const updatedRequired = [...requiredEntries];
    updatedRequired[idx].key = newKey;

    const updatedOrderKeys = [...orderKeys];
    updatedOrderKeys[idx] = newKey;

    setRequiredEntries(updatedRequired);
    setPropertyEntries(updatedEntries);
    setOrderKeys(updatedOrderKeys);

    const hasDuplicateKeys =
      new Set(updatedEntries.map(([k]) => k)).size !== updatedEntries.length;

    if (!hasDuplicateKeys) {
      const newRequired = requiredEntriesToRequired(updatedRequired);
      const updatedValue = {
        ...value,
        required: newRequired,
        properties: Object.fromEntries(updatedEntries),
      };
      onChange(updatedValue);
    }
  };

  const handleRequiredChange = (required: boolean, idx: number) => {
    const updatedRequired = [...requiredEntries];
    updatedRequired[idx].required = required;
    setRequiredEntries(updatedRequired);

    const newRequired = requiredEntriesToRequired(updatedRequired);
    onChange({
      ...value,
      required: newRequired,
    });
  };

  const handleDelete = (idx: number) => {
    const deletedKey = propertyEntries[idx][0];
    const filterPredicate = (_, prevIdx) => idx !== prevIdx;
    const updatedEntries = propertyEntries.filter(filterPredicate);
    const updatedOrderKeys = orderKeys.filter(filterPredicate);
    const updatedRequired = requiredEntries.filter(filterPredicate);

    setPropertyEntries(updatedEntries);
    setOrderKeys(updatedOrderKeys);
    setRequiredEntries(updatedRequired);

    // If we have transformations, clean up any that were associated with the deleted property
    if (setTransformations && transformations) {
      const newTransformations = mapTransformationPathMatches(
        deletedKey,
        path,
        transformations,
        () => null // Return null to remove transformations matching the deleted property path
      );
      setTransformations(newTransformations);
    }

    const newProperties = Object.fromEntries(updatedEntries);
    onChange({
      ...value,
      properties: newProperties,
      required: requiredEntriesToRequired(updatedRequired),
    });
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        variant !== SchemaEditorVariant.FUNCTIONS && 'bg-transparent p-0',
        variant === SchemaEditorVariant.NESTED && 'pb-2'
      )}
    >
      {orderKeys.map((key, idx) => {
        const [, property] = propertyEntries[idx];

        const isDuplicate =
          propertyEntries.filter(([k]) => k === key).length > 1;

        const onInputChange = (e) => {
          const newKey = e.target.value;
          handleInputChange(newKey, idx);
        };

        const onRequiredChange = (e) => {
          handleRequiredChange(e.target.checked, idx);
        };

        const onDelete = () => {
          handleDelete(idx);
        };

        const onItemChange = (newValue) => {
          handleChildChange(newValue, idx);
        };

        return (
          <ParametersEditorView
            key={idx}
            value={property}
            variant={variant}
            readOnly={readOnly}
            codeEditorExtensions={codeEditorExtensions}
            propertyKey={key}
            onInputChange={onInputChange}
            onRequiredChange={onRequiredChange}
            onDelete={onDelete}
            onItemChange={onItemChange}
            nestingLevel={nestingLevel}
            required={requiredEntries[idx].required}
            isDuplicate={isDuplicate}
            path={path}
            transformations={transformations}
            setTransformations={setTransformations}
          />
        );
      })}

      {!readOnly && (
        <AddPropertyToObjectButton
          value={value}
          onChange={(newValue, newKey) => {
            setPropertyEntries((prev) => [
              ...prev,
              [newKey, { type: 'string' }],
            ]);
            setOrderKeys([...orderKeys, newKey]);
            setRequiredEntries((prev) => [
              ...prev,
              { key: newKey, required: false },
            ]);
            onChange(newValue);
          }}
          newItemType={
            variant === SchemaEditorVariant.EVALUATOR ? 'boolean' : 'string'
          }
          addLabel={`Add ${VARIANT_LABEL_MAP[variant]}`}
        />
      )}

      <div className="flex items-center gap-2">
        <Checkbox
          checked={value.additionalProperties !== false}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(e) => {
            onChange({
              ...value,
              additionalProperties: e.target.checked,
            });
          }}
        />
        <label htmlFor="additionalProperties" className="text-sm">
          Allow additional properties
        </label>
        <Tooltip
          title={
            value.additionalProperties === false
              ? 'additionalProperties: false (disallows properties not defined in the schema)'
              : 'additionalProperties: true (allows properties not defined in the schema)'
          }
          side="bottom"
        >
          <span>
            <InfoCircleIcon className="h-3.5 w-3.5 text-disabled" />
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

export function EditInputFunctionParameters({
  value,
  onChange,
  actions,
  variant = SchemaEditorVariant.FUNCTIONS,
  readOnly = false,
  hideTitle = false,
  showEditorSelector = false,
  codeEditorExtensions,
  showBorder: border = false,
  transformations,
  setTransformations,
  pathPrefix,
}: {
  value: unknown;
  onChange?: (value: unknown) => void;
  actions?: React.ReactNode;
  variant?: SchemaEditorVariant;
  readOnly?: boolean;
  hideTitle?: boolean;
  showEditorSelector?: boolean;
  codeEditorExtensions?: Record<string, Extension[]>;
  showBorder?: boolean;
  transformations?: DatasetTransformationSchema[];
  setTransformations?: (transformations: DatasetTransformationSchema[]) => void;
  pathPrefix?: string[];
}) {
  const isEditorAvailable = isJsonSchema7(value) && value.type === 'object';
  const [isEditor, setIsEditor] = useState(isEditorAvailable);
  const [language, setLanguage] = useLanguagePreference();
  const mode = isEditor && isEditorAvailable ? ('editor' as const) : language;

  return (
    <div
      className={cn(
        'group flex flex-col rounded-md bg-background p-1',
        border && 'border border-secondary py-3'
      )}
    >
      {[SchemaEditorVariant.SCHEMA, SchemaEditorVariant.EVALUATOR].includes(
        variant
      ) &&
        !hideTitle &&
        isEditorAvailable &&
        (!readOnly || !!value.title || !!value.description) && (
          <>
            <div className="grid grid-cols-[1fr,auto]">
              <input
                value={value.title ?? ''}
                className="border-none bg-transparent px-3.5 py-0.5 font-mono font-medium placeholder:text-disabled focus:outline-none"
                placeholder="Title"
                readOnly={readOnly}
                onChange={(event) => {
                  onChange?.({ ...value, title: event.target.value });
                }}
              />

              <div className="mr-2 flex flex-shrink-0 -translate-x-1 translate-y-1 items-center gap-2 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                <CopyIconButton copy={JSON.stringify(value, null, 2)} />
              </div>
            </div>

            <PlainTextEditor
              value={value.description ?? ''}
              readOnly={readOnly}
              placeholder="Description"
              className="bg-transparent px-[13px] py-0.5 text-sm text-tertiary focus:outline-none"
              textareaClassName="text-sm"
              onChange={(description) => onChange?.({ ...value, description })}
            />
          </>
        )}

      <div className="mx-4 mt-2">
        {mode === 'editor' && isEditorAvailable ? (
          <FunctionParametersEditor
            value={value}
            onChange={onChange ?? (() => {})}
            variant={variant}
            readOnly={readOnly}
            codeEditorExtensions={codeEditorExtensions}
            nestingLevel={0}
            path={pathPrefix ?? []}
            transformations={transformations}
            setTransformations={setTransformations}
          />
        ) : (
          <CodeDataEditor
            value={value}
            language={language}
            onChange={(parameters) => onChange?.(parameters)}
            readOnly={readOnly}
            extensions={codeEditorExtensions?.[language] ?? []}
          />
        )}
        {setTransformations && isEditorAvailable && (
          <TransformationsEditor
            path={pathPrefix ?? []}
            transformations={transformations}
            setTransformations={setTransformations}
            value={value}
          />
        )}
        {(actions || showEditorSelector) && (
          <div className="-ml-4 -mr-2 flex items-center justify-between gap-2 border-secondary empty:hidden">
            <Select
              value={mode}
              onChange={(_, value) => {
                if (value == null) return;

                if (value === 'editor') {
                  setIsEditor(true);
                } else {
                  setIsEditor(false);
                  setLanguage(value);
                }
              }}
              size="sm"
              slotProps={{
                root: {
                  sx: {
                    marginTop: 1,
                    marginBottom: 1,
                    marginLeft: 1,
                    width: 'unset',
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: 'small',
                  },
                },
                button: {
                  sx: (theme) => ({
                    color: theme.vars.palette.neutral[500],
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }),
                },
              }}
            >
              <Option disabled={!isEditorAvailable} value="editor">
                Editor
              </Option>
              <Option value="json">JSON</Option>
              <Option value="yaml">YAML</Option>
            </Select>

            <div className="mr-2 flex items-center justify-end gap-2">
              {actions}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function EditInputFunction(props: {
  value: OpenAIFunction;
  onChange: (value: OpenAIFunction) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  onRemove: () => void;
  readOnly?: boolean;
  rightActions?: React.ReactNode;
}) {
  return (
    <div
      className={
        'group flex flex-col overflow-hidden rounded-lg border border-secondary bg-background'
      }
    >
      <div className="flex flex-col py-3 pb-0">
        <div className="grid grid-cols-[1fr,auto]">
          <input
            value={props.value.name ?? ''}
            className="bg-transparent py-0.5 pl-4 font-mono font-medium placeholder:text-disabled focus:outline-none"
            placeholder="Function name..."
            onChange={(event) => {
              props.onChange({ ...props.value, name: event.target.value });
            }}
            data-testid="tool-name-edit-field"
          />

          <div className="mr-2 flex flex-shrink-0 -translate-x-1 translate-y-1 items-center gap-2 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
            <CopyIconButton copy={JSON.stringify(props.value, null, 2)} />
            {!props.readOnly && (
              <Tooltip title="Delete" side="top">
                <button
                  type="button"
                  onClick={props.onRemove}
                  className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-secondary p-1 transition-all hover:bg-secondary-hover active:bg-secondary"
                >
                  <TrashIcon />
                </button>
              </Tooltip>
            )}
            {props.rightActions}
          </div>
        </div>

        <PlainTextEditor
          value={props.value.description ?? ''}
          placeholder="Describe your function..."
          className="bg-transparent py-0.5 pl-4 text-disabled focus:outline-none"
          onChange={(description) => {
            props.onChange({ ...props.value, description });
          }}
          textareaClassName="text-sm"
        />
      </div>

      <EditInputFunctionParameters
        value={props.value.parameters}
        onChange={(parameters) =>
          props.onChange({ ...props.value, parameters })
        }
        actions={
          !props.readOnly ? (
            <>
              {props.onCancel && (
                <button
                  className="rounded-md border border-secondary px-3 py-1 text-sm font-semibold hover:bg-secondary-hover active:bg-secondary"
                  type="button"
                  onClick={props.onCancel}
                >
                  Cancel
                </button>
              )}
              {props.onSubmit && (
                <button
                  className="rounded-md border border-transparent bg-brand-green-400 px-3 py-1 text-sm font-semibold text-white"
                  type="button"
                  onClick={props.onSubmit}
                  data-testid="save-tool-button"
                >
                  Save
                </button>
              )}
            </>
          ) : null
        }
        readOnly={props.readOnly}
      />
    </div>
  );
}
